/**
 * Pure helpers that transform a flat `{formKey: formValue}` inflection map
 * (emitted by polyreader-nlp's morphology adapters) into a structured list
 * of display tables. No React, no JSX — the rendering lives in
 * `VocabEntryCard.tsx` so this module can be unit-tested as plain data.
 *
 * Shape summary:
 *   - nouns / propn : one `Sg | Pl` table. Slavic languages produce a
 *     row per case; English-style flat (just `sg` / `pl`) produces a
 *     single row with an extras block for anything unexpected.
 *   - verbs : one table per tense bucket (present, preterite, past,
 *     future, imperative). Romance and Slavic produce person rows
 *     (1sg..3pl) per tense; Slavic past is special and produces gender
 *     rows instead. Anything not matching a known tense lands in a
 *     trailing "Forms" table (English lemminflect verbs end up entirely
 *     there since their keys are flat: base / 3sg_pres / past / past_part
 *     / pres_part).
 *   - adjectives : one table per gender that appears in the data. Cases
 *     run down the rows, sg/pl across the columns. Ungendered plural
 *     (`<case>_pl`) is used as a fallback for languages where the
 *     plural collapses across genders (Russian-style).
 *   - unknown POS : a single untitled table dumping every key/value pair.
 */

import type { InflectionBlock } from '../articles/vocab-store';

/**
 * A single inflection table to render. Verbs produce multiple tables
 * side by side (one per tense). Nouns/adjectives produce one table.
 */
export interface InflectionTable {
  /** Title shown above the table; empty string suppresses the title row. */
  title: string;
  /** Header labels above each form column. Empty string = no header for that column. */
  columns: string[];
  /** Each row has a row-label cell and one cell per column. */
  rows: { label: string; cells: string[] }[];
}

export const NOUN_CASES: { key: string; label: string }[] = [
  { key: 'nom', label: 'Nom' },
  { key: 'gen', label: 'Gen' },
  { key: 'dat', label: 'Dat' },
  { key: 'acc', label: 'Acc' },
  { key: 'ins', label: 'Ins' },
  { key: 'loc', label: 'Loc' },
  { key: 'voc', label: 'Voc' },
];

/**
 * Verb tense buckets in display order. Each bucket becomes a separate
 * table. Anything that doesn't match a known prefix lands in the trailing
 * "Forms" table.
 */
export const VERB_TENSE_BUCKETS: { key: string; label: string }[] = [
  { key: 'present',    label: 'Present' },
  { key: 'preterite',  label: 'Preterite' },
  { key: 'imperfect',  label: 'Imperfect' },
  { key: 'past',       label: 'Past' },
  { key: 'future',     label: 'Future' },
  { key: 'imperative', label: 'Imperative' },
];

export const PERSON_ORDER = ['1sg', '2sg', '3sg', '1pl', '2pl', '3pl'];
export const GENDER_ORDER = ['masc', 'fem', 'neut', 'pl'];

/**
 * Display label for a gender code that appears in form keys. Covers the
 * common short and long forms used by morphology libraries; unknown codes
 * fall back to a title-cased version of the code itself.
 */
export const GENDER_LABELS: Record<string, string> = {
  m: 'Masculine',     masc: 'Masculine',
  f: 'Feminine',      fem:  'Feminine',
  n: 'Neuter',        neut: 'Neuter',
  c: 'Common',        com:  'Common',
};

/** Preferred display order for gender tables. Anything else after, alphabetically. */
export const GENDER_ORDER_PREF = ['m', 'masc', 'f', 'fem', 'n', 'neut', 'c', 'com'];

export function genderLabel(code: string): string {
  return GENDER_LABELS[code] ?? code.charAt(0).toUpperCase() + code.slice(1);
}

export function genderSortKey(code: string): number {
  const i = GENDER_ORDER_PREF.indexOf(code);
  return i === -1 ? GENDER_ORDER_PREF.length : i;
}

export function buildNounTables(forms: Record<string, string>): InflectionTable[] {
  // English-style flat: just sg / pl with no case prefix.
  const hasAnyCase = Object.keys(forms).some(k =>
    NOUN_CASES.some(c => k === `${c.key}_sg` || k === `${c.key}_pl`)
  );

  if (!hasAnyCase) {
    // Single row, two cells (sg / pl) — anything else goes underneath as
    // bare key/value pairs.
    const cells: string[] = [forms.sg ?? '—', forms.pl ?? '—'];
    const knownKeys = new Set(['sg', 'pl']);
    const extraRows = Object.entries(forms)
      .filter(([k]) => !knownKeys.has(k))
      .map(([k, v]) => ({ label: k.replace(/_/g, ' '), cells: [v, ''] }));
    return [{
      title: '',
      columns: ['Sg', 'Pl'],
      rows: [{ label: '', cells }, ...extraRows],
    }];
  }

  // Russian/Polish case × number table.
  const rows: { label: string; cells: string[] }[] = [];
  for (const c of NOUN_CASES) {
    const sg = forms[`${c.key}_sg`];
    const pl = forms[`${c.key}_pl`];
    if (sg || pl) {
      rows.push({ label: c.label, cells: [sg || '—', pl || '—'] });
    }
  }
  return [{ title: '', columns: ['Sg', 'Pl'], rows }];
}

export function buildVerbTables(forms: Record<string, string>): InflectionTable[] {
  const tables: InflectionTable[] = [];
  const usedKeys = new Set<string>();

  for (const bucket of VERB_TENSE_BUCKETS) {
    // Try person ordering first (1sg..3pl).
    const personRows: { label: string; cells: string[] }[] = [];
    for (const person of PERSON_ORDER) {
      const key = `${bucket.key}_${person}`;
      const value = forms[key];
      if (value) {
        personRows.push({ label: person, cells: [value] });
        usedKeys.add(key);
      }
    }

    if (personRows.length > 0) {
      tables.push({ title: bucket.label, columns: [''], rows: personRows });
      continue;
    }

    // Russian/Polish past tense agrees with subject gender, not person.
    if (bucket.key === 'past') {
      const genderRows: { label: string; cells: string[] }[] = [];
      for (const gender of GENDER_ORDER) {
        const key = `${bucket.key}_${gender}`;
        const value = forms[key];
        if (value) {
          genderRows.push({ label: gender, cells: [value] });
          usedKeys.add(key);
        }
      }
      if (genderRows.length > 0) {
        tables.push({ title: bucket.label, columns: [''], rows: genderRows });
      }
    }
  }

  // Anything we didn't slot into a tense bucket — infinitive, past_participle,
  // English flat forms (base, 3sg_pres, past_part, pres_part), etc.
  const otherRows: { label: string; cells: string[] }[] = [];
  for (const [key, value] of Object.entries(forms)) {
    if (!usedKeys.has(key)) {
      otherRows.push({ label: key.replace(/_/g, ' '), cells: [value] });
    }
  }
  if (otherRows.length > 0) {
    tables.push({ title: 'Forms', columns: [''], rows: otherRows });
  }

  return tables;
}

/**
 * Adjectives with case×gender×number keys (`nom_sg_m`, `nom_sg_f`,
 * `nom_pl`, etc.) render as one titled table per gender that appears in
 * the data. Cases and genders are discovered from the keys themselves —
 * no hard-coded language-specific gender set — so this works for Russian
 * (m/f/n with genderless plural), Polish (with vocative), Romance (m/f),
 * Scandinavian (common+neuter), and anything else with a similar shape.
 *
 * Plural lookup falls back from gendered (`<case>_pl_<g>`) to ungendered
 * (`<case>_pl`) so Russian's genderless plural still surfaces in every
 * gender's table. English flat keys (base/comparative/superlative) don't
 * match the pattern and drop to the simple dump fallback.
 */
export function buildAdjectiveTables(forms: Record<string, string>): InflectionTable[] {
  const ENTRY_PATTERN = /^([a-z]+)_(sg|pl)(?:_([a-z]+))?$/;

  // Discover which cases and genders appear in the data.
  const casesSeen = new Set<string>();
  const gendersSeen = new Set<string>();
  let patternHits = 0;
  for (const key of Object.keys(forms)) {
    const m = key.match(ENTRY_PATTERN);
    if (!m) continue;
    patternHits++;
    casesSeen.add(m[1]);
    if (m[3]) gendersSeen.add(m[3]);
  }

  // Need at least one gendered key to justify a per-gender layout.
  if (gendersSeen.size === 0 || patternHits === 0) {
    return [{
      title: '',
      columns: [''],
      rows: Object.entries(forms).map(([k, v]) => ({
        label: k.replace(/_/g, ' '),
        cells: [v],
      })),
    }];
  }

  // Order cases: known NOUN_CASES first in their canonical order, then any
  // unknown cases the data contains, alphabetically.
  const knownOrder = NOUN_CASES.filter(c => casesSeen.has(c.key));
  const knownKeys = new Set(knownOrder.map(c => c.key));
  const extraCases = [...casesSeen]
    .filter(c => !knownKeys.has(c))
    .sort()
    .map(c => ({ key: c, label: c.charAt(0).toUpperCase() + c.slice(1) }));
  const cases = [...knownOrder, ...extraCases];

  const genders = [...gendersSeen].sort(
    (a, b) => genderSortKey(a) - genderSortKey(b) || a.localeCompare(b)
  );

  const tables: InflectionTable[] = [];
  for (const gender of genders) {
    const rows: { label: string; cells: string[] }[] = [];
    for (const c of cases) {
      const sg = forms[`${c.key}_sg_${gender}`];
      // Prefer gendered plural if the adapter ships one; otherwise fall
      // back to the ungendered <case>_pl (Russian-style).
      const pl = forms[`${c.key}_pl_${gender}`] ?? forms[`${c.key}_pl`];
      if (sg || pl) {
        rows.push({ label: c.label, cells: [sg || '—', pl || '—'] });
      }
    }
    if (rows.length > 0) {
      tables.push({ title: genderLabel(gender), columns: ['Sg', 'Pl'], rows });
    }
  }
  return tables;
}

export function buildTables(inflections: InflectionBlock): InflectionTable[] {
  const entries = Object.entries(inflections.forms);
  if (entries.length === 0) return [];

  if (inflections.type === 'verb') {
    return buildVerbTables(inflections.forms);
  }
  if (inflections.type === 'noun' || inflections.type === 'propn') {
    return buildNounTables(inflections.forms);
  }
  if (inflections.type === 'adj') {
    return buildAdjectiveTables(inflections.forms);
  }
  // Unknown types: dump into one untitled single-column table.
  return [{
    title: '',
    columns: [''],
    rows: entries.map(([k, v]) => ({ label: k.replace(/_/g, ' '), cells: [v] })),
  }];
}
